import { useState, useCallback, type FC } from 'react';
import { createPortal } from 'react-dom';
import { Sparkles, TrendingUp, Inbox, Settings, ArrowRight, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

interface WelcomeTourProps {
  isOpen: boolean;
  onClose: () => void;
}

interface Slide {
  icon: LucideIcon;
  title: string;
  description: string;
  details?: string[];
}

const SLIDES: Slide[] = [
  {
    icon: Sparkles,
    title: 'Bienvenue sur Feedback Orchestrator',
    description:
      'Votre plateforme centralisee de Product Intelligence. Collectez, analysez et exploitez les feedbacks utilisateurs grace a l\'IA.',
  },
  {
    icon: TrendingUp,
    title: 'Votre centre de commande Business',
    description:
      'Le pole Business regroupe 9 modules Growth pour piloter votre strategie de croissance.',
    details: [
      'Content Factory — Generation de contenu IA',
      'Sales Intelligence — Fiches argumentaires et prospection',
      'Knowledge Explorer — Base de connaissances',
      'Blog Manager — Articles SEO auto-generes',
      'Content Calendar — Planification editoriale',
      'Campaigns — Suivi des campagnes marketing',
      'Referral Network — Systeme de parrainage',
    ],
  },
  {
    icon: Inbox,
    title: 'Pilotez votre relation client',
    description:
      'Le pole Customer Success centralise les feedbacks et l\'annuaire clients.',
    details: [
      'Feedback Inbox — Analyse IA de chaque retour utilisateur',
      'Annuaire Clients — Vue 360 de chaque utilisateur',
    ],
  },
  {
    icon: Settings,
    title: 'Gerez vos operations',
    description:
      'Le pole Operations offre une vue complete de l\'infrastructure et de la facturation.',
    details: [
      'Organisations — Gestion des comptes clients',
      'Abonnements — MRR, plans et facturation Stripe',
      'Token Watcher — Surveillance de la consommation IA',
      'BYOK Manager — Cles API Enterprise',
    ],
  },
  {
    icon: ArrowRight,
    title: 'C\'est parti !',
    description:
      'Explorez le Growth Dashboard pour commencer. Vous pouvez a tout moment cliquer sur le bouton d\'aide dans le header pour revoir les tours guides.',
  },
];

export const WelcomeTour: FC<WelcomeTourProps> = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(0);

  const handleNext = useCallback(() => {
    if (step >= SLIDES.length - 1) {
      onClose();
    } else {
      setStep((s) => s + 1);
    }
  }, [step, onClose]);

  const handlePrev = useCallback(() => {
    setStep((s) => Math.max(0, s - 1));
  }, []);

  if (!isOpen) return null;

  const slide = SLIDES[step];
  const Icon = slide.icon;

  const modal = (
    <div className="fixed inset-0 z-[10100] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="relative w-full max-w-lg mx-4 rounded-2xl overflow-hidden shadow-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 p-1.5 rounded-lg text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Header gradient */}
        <div className="h-28 bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-700 flex items-center justify-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/20 backdrop-blur-sm">
            <Icon className="w-7 h-7 text-white" />
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-5">
          <h2 className="text-lg font-bold text-slate-900 dark:text-white">
            {slide.title}
          </h2>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
            {slide.description}
          </p>
          {slide.details && (
            <ul className="mt-3 space-y-1.5">
              {slide.details.map((detail, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 text-xs text-slate-500 dark:text-slate-400"
                >
                  <span className="mt-1 flex h-1.5 w-1.5 flex-shrink-0 rounded-full bg-blue-500 dark:bg-blue-400" />
                  {detail}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-slate-100 dark:border-slate-700 px-6 py-3">
          {/* Dots */}
          <div className="flex gap-1.5">
            {SLIDES.map((_, i) => (
              <button
                key={i}
                onClick={() => setStep(i)}
                className={`h-2 rounded-full transition-all duration-200 ${
                  i === step
                    ? 'w-6 bg-blue-500 dark:bg-blue-400'
                    : 'w-2 bg-slate-200 dark:bg-slate-600 hover:bg-slate-300 dark:hover:bg-slate-500'
                }`}
              />
            ))}
          </div>

          {/* Buttons */}
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-3 py-1.5 rounded-lg text-xs font-medium text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
            >
              Passer
            </button>
            {step > 0 && (
              <button
                onClick={handlePrev}
                className="px-3 py-1.5 rounded-lg text-xs font-medium text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
              >
                Precedent
              </button>
            )}
            <button
              onClick={handleNext}
              className="px-4 py-1.5 rounded-lg text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 transition-colors"
            >
              {step === SLIDES.length - 1 ? 'Explorer' : 'Suivant'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default WelcomeTour;
